import { Address, CompanySpecificService, Contract, ContractCompanySpecificService, ContractLight } from "@/types";
import { CreateOrderFromContractValues } from "@/components/organisms/CreateOrderFromContract/index";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { findDeliveryAddressObject } from "@/utils/AddressHelper";

export const createOrderFromContractPlaceholder = (
  companyId?: number,
  userId?: number,
  contract?: ContractLight
): Partial<CreateOrderFromContractValues> | null => {
  if (!companyId || !userId || !contract) return null;
  return {
    companyId: companyId,
    userId: userId,
    amount: 0,
    unit: contract.contractProduct?.priceUnit.id ?? 1,
    orderAddress: null,
    contractId: contract.id,
    boxtypeId: 1,
    orderCurrency: contract.contractProduct?.currencyType,
    orderNeedsApproval: !contract.automaticSalesApproval,
    servicePriceOverrides: {},
    estimatedDelivery: new Date(),
    created: new Date(),
    batchNumber: "",
    latitude: "",
    longitude: "",
    capturedAt: null,
  };
};

export const getMappedServicesWithContractPricing = (
  companySpecificServicesData: CompanySpecificService[] | undefined,
  fullContract: Contract
): ContractCompanySpecificService[] => {
  if (!companySpecificServicesData || !fullContract?.companySpecificServices) return [];

  return companySpecificServicesData
    .filter(
      (service) =>
        service.priceUnit.code === "PPO" &&
        fullContract.companySpecificServices.some((s) => s.companySpecificServiceId === service.id)
    )
    .map((service) => {
      const contractService = fullContract.companySpecificServices.find(
        (s) => s.companySpecificServiceId === service.id
      );

      return {
        companySpecificServiceId: service.id,
        price: contractService?.price || service.price,
        currencyType: service.currency,
      };
    });
};

/** Swaps in the prices the user edited in the wizard, keyed by companySpecificServiceId. */
export const applyServicePriceOverrides = (
  services: ContractCompanySpecificService[],
  overrides: Record<number, number>
): ContractCompanySpecificService[] =>
  services.map((service) =>
    service.companySpecificServiceId in overrides
      ? { ...service, price: overrides[service.companySpecificServiceId] }
      : service
  );

export const determineIfEstimatedDeliveryIsRequired = (orderPlanning: boolean, automaticProccessing: boolean) => {
  return orderPlanning || automaticProccessing;
};

/**
 * The addresses an order uses unless the user picks another one. Prefers the address that was
 * selected when the contract was created; falls back to the company's current delivery address
 * only if the contract has none set.
 */
export const getContractDeliveryAddresses = (
  contract: Contract,
  tradeType: ContractTradeTypeEnum
): { ownAddress: Address | undefined; counterpartAddress: Address | undefined } => {
  const deliverFrom = contract.transportFromAddress?.address
    ? contract.transportFromAddress
    : findDeliveryAddressObject(contract.contractSellers[0]?.companyAddress ?? []);
  const deliverTo = contract.transportToAddress?.address
    ? contract.transportToAddress
    : findDeliveryAddressObject(contract.contractBuyers[0]?.companyAddress ?? []);

  return tradeType === ContractTradeTypeEnum.BUY
    ? { ownAddress: deliverTo, counterpartAddress: deliverFrom }
    : { ownAddress: deliverFrom, counterpartAddress: deliverTo };
};

/**
 * Maps the counterpart's chosen shipping address to the BE field for their side: on a purchase
 * order they are the seller (Deliver from), on a sales order the buyer (Deliver to).
 */
export const getTransportAddressIds = (tradeType: ContractTradeTypeEnum, counterpartAddressId: number | undefined) => ({
  transportFromAddressId: tradeType === ContractTradeTypeEnum.BUY ? (counterpartAddressId ?? null) : null,
  transportToAddressId: tradeType === ContractTradeTypeEnum.SELL ? (counterpartAddressId ?? null) : null,
});

export type OrderCreationErrorReason =
  | "slotSelectionRequired"
  | "estimatedDeliveryInvalid"
  | "slotUnavailable"
  | "slotFull"
  | "slotFractionMismatch"
  | "generic";

/**
 * Maps a slot-reservation/order-creation failure to an actionable UI reason, per the EOP
 * handoff's status table (§12). 400 covers two distinct backend rejections that share a status
 * code — they're disambiguated using whether a slot was submitted, since the backend never
 * exposes a distinguishing error code/message for this case.
 */
export const mapOrderCreationError = (
  status: number | undefined,
  receptionSlotId: number | null
): OrderCreationErrorReason => {
  switch (status) {
    case 400:
      return receptionSlotId ? "estimatedDeliveryInvalid" : "slotSelectionRequired";
    case 404:
      return "slotUnavailable";
    case 409:
      return "slotFull";
    case 422:
      return "slotFractionMismatch";
    default:
      return "generic";
  }
};
